import { useContext, useEffect } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import _ from "lodash";
import cx from "classnames";

import InfiniteScroll from "src/components/infiniteScroll";
import Text from "src/components/text";

import { DealRoomRecipient } from "src/types/dealRoom";
import { DealRoomActionPlanContext } from "../../../../context";

import styles from "./styles.module.scss";
const DefaultUserIcon = "/images/default_user_profile_background.png";
const EmptyRecipientsImage = "/images/dealRoom/emptyRecipients.svg";

const getRecipientName = (recipient: DealRoomRecipient) => {
  const isContact = Object.hasOwn(recipient, "contact");
  const selector = isContact ? "contact" : "user";

  return (
    `${_.get(recipient, `[${selector}].firstName`, "")} ` +
    `${_.get(recipient, `[${selector}].lastName`, "")}`
  );
};

const getRecipientIcon = (recipient: DealRoomRecipient) => {
  const isContact = Object.hasOwn(recipient, "contact");
  const selector = isContact ? "contact" : "user";

  return _.get(recipient, `[${selector}].icon.thumbnail`);
};

const getButtonText = (recipients: DealRoomRecipient[]) => {
  if (_.size(recipients) === 0) {
    return <p className={styles.buttonText}>Assign</p>;
  }

  const isContact = Object.hasOwn(recipients[0], "contact");
  const selector = isContact ? "contact" : "user";

  if (_.size(recipients) > 1) {
    return (
      <p className={styles.buttonText}>
        <span className={styles.linesEllipsis}>
          {`${_.get(recipients, `[0][${selector}].firstName`, "")} ` +
          `${_.get(recipients, `[0][${selector}].lastName`, "")} `}
        </span>
        {" "}
        <span className={styles.count}>
        {" "}{`+ ${_.size(recipients) - 1}`}
        </span>
      </p>
    );
  }

  return (
    <p className={styles.buttonText}>
      <span className={styles.linesEllipsis}>
      {`${_.get(recipients, `[0][${selector}].firstName`, "")} ` +
        `${_.get(recipients, `[0][${selector}].lastName`, "")}`}
      </span>
    </p>
  );
};

const Recipient = ({ recipient, isSelected, onToggle, disabled = false, editMode }) => {
  const recipientName = getRecipientName(recipient);
  const recipientIcon = getRecipientIcon(recipient);

  const isRecipientRemoved = !_.get(recipient, "active", true);
  const isRecipientExternal = Object.hasOwn(recipient, "contact");

  return (
    <div className={styles.recipientContainer}  onClick={() => onToggle(recipient, !isSelected)}>
      <div className={styles.leftContainer}>
        <img
          src={recipientIcon ?? DefaultUserIcon}
          className={styles.userIcon}
        />
        <div className={styles.textContainer}>
          <Text
            text={recipientName}
            className={cx(styles.recipientName, {
              [styles.removed]: isRecipientRemoved,
            })}
            lines={1}
          />
        </div>
        {isRecipientRemoved && (
          <p
            className={cx(styles.recipientName, {
              [styles.removed]: isRecipientRemoved,
            })}
          >
            (Removed)
          </p>
        )}
        {isRecipientExternal && !isRecipientRemoved && (
          <div className={styles.externalTag}>external</div>
        )}
      </div>
      {(!isRecipientRemoved && editMode) && (
        <div className={styles.rightContainer}>
          <i
            className={cx(styles.tickIcon, {
              "paperflite-tick-selected": isSelected,
              "paperflite-radio-button-unselected": !isSelected,
              [styles.unselected]: !isSelected,
              [styles.disabled]: disabled || isRecipientRemoved,
            })}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
};

interface AssigneesPopoverProps {
  dealRoomId: string;
  stageId: string;
  actionId: string;
  selectedRecipients: DealRoomRecipient[];
  onSelectRecipient: (
    stageId: string,
    actionId: string,
    isNewAction: boolean,
    recipient: DealRoomRecipient,
    isAdded: boolean,
  ) => void;
  onPopoverClose?: () => void;
  isNewAction: boolean;
  disabled: boolean;
  canEditAction: boolean;
  isActionCompleted: boolean;
}

const AssigneesPopover = ({
  dealRoomId,
  stageId,
  actionId,
  selectedRecipients,
  onSelectRecipient = (
    stageId: string,
    actionId: string,
    isNewAction: boolean,
    recipient: DealRoomRecipient,
    isAdded: boolean,
  ) => {},
  onPopoverClose,
  isNewAction,
  disabled,
  canEditAction,
  isActionCompleted,
}: AssigneesPopoverProps) => {
  const { state, handleFetchRecipients } = useContext(
    DealRoomActionPlanContext,
  );

  const editMode = _.get(state, "editMode", false);

  const recipients = _.get(state, "recipients");
  const recipientItems = _.get(recipients, "items", []);
  const loading = _.get(recipients, "loading", false);

  const showUserProfilePicture = _.size(selectedRecipients) > 0;

  const buttonText = getButtonText(selectedRecipients);

  const loadMoreRecipients = () => {
    const nextPage = currentPage + 1;
    if (nextPage < totalPages) {
      handleFetchRecipients(dealRoomId, nextPage);
    }
  };

  const pagination = _.get(recipients, "pagination", {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 20,
    totalRecordsCount: 0,
    hasMoreItems: false,
  });
  const { currentPage, totalPages, hasMoreItems } = pagination;

  useEffect(() => {
    handleFetchRecipients(dealRoomId, 0);
  }, []);

  const getIsRecipientSelected = (targetRecipient: DealRoomRecipient) => {
    const isContact = Object.hasOwn(targetRecipient, "contact");
    const selector = isContact ? "contact" : "user";

    if (_.size(selectedRecipients) === 0) {
      return false;
    }

    return !!_.find(selectedRecipients, {
      [selector]: { id: targetRecipient?.[selector]?.id },
    });
  };

  const handleOnToggle = (recipient: DealRoomRecipient, isAdded: boolean) => {
    const isRecipientRemoved = !_.get(recipient, "active");
    if (!editMode || isRecipientRemoved) {
      return;
    }

    onSelectRecipient(stageId, actionId, isNewAction, recipient, isAdded);
  };

  return (
    <OverlayTrigger
      trigger="click"
      placement="left"
      rootClose
      container={document.body}
      onToggle={(nextShow) => {
        if (!nextShow && onPopoverClose) {
          onPopoverClose();
        }
      }}
      overlay={
        <Popover id={`popover-${actionId}`} className={styles.popover}>
            <Popover.Body>
              <div
                className={cx(styles.container, {
                  [styles.disabled]: !canEditAction || isActionCompleted,
                })}
              >
                <p className={styles.heading}>Deal Room Stakeholders</p>
                {!editMode && (
                  <div className={styles.recipientsContainer}>
                    {selectedRecipients?.map((recipient: DealRoomRecipient) => (
                      <Recipient
                        recipient={recipient}
                        isSelected={getIsRecipientSelected(recipient)}
                        onToggle={handleOnToggle}
                        disabled={!editMode || isActionCompleted}
                        editMode={editMode}
                      />
                    ))}
                  </div>
                )}

                {editMode && _.size(recipientItems) === 0 && (
                  <div className={styles.emptyRecipients}>
                    <img className={styles.image} src={EmptyRecipientsImage} />
                    <p className={styles.subtext}>
                      There are no stakeholders currently. Add stakeholders to
                      your deal room and assign them action items.
                    </p>
                  </div>
                )}
                
                {editMode && _.size(recipientItems) > 0 && (
                  <InfiniteScroll
                    hasMore={hasMoreItems}
                    loading={loading}
                    loadMore={loadMoreRecipients}
                    className={styles.recipientsContainer}
                  >
                    {recipientItems?.map((recipient: DealRoomRecipient) => (
                      <Recipient
                        recipient={recipient}
                        isSelected={getIsRecipientSelected(recipient)}
                        onToggle={handleOnToggle}
                        disabled={!editMode || isActionCompleted}
                        editMode={editMode}
                      />
                    ))}
                  </InfiniteScroll>
                )}
              </div>
            </Popover.Body>
          </Popover>
      }
    >
      <div
        className={cx(styles.actionButton, {
          [styles.disabled]:
            !canEditAction ||
            isActionCompleted
        })}
      >
        {showUserProfilePicture ? (
          <img
            className={styles.userIcon}
            src={getRecipientIcon(selectedRecipients[0]) ?? DefaultUserIcon}
          />
        ) : (
          <i className={cx(styles.icon, "paperflite-add-assignee")} />
        )}
        {buttonText}
      </div>
    </OverlayTrigger>
  );
};

export default AssigneesPopover;
